import { useState, useEffect, useCallback } from 'react'
import {
  MdArrowBack,
  MdPlayArrow,
  MdPause,
  MdStop,
  MdSchedule,
  MdChat,
  MdPerson,
  MdEdit,
  MdDelete,
  MdRefresh,
  MdTag,
  MdFlight,
  MdAirplanemodeActive,
  MdLocationOn,
  MdBusiness,
  MdWork,
  MdLuggage,
  MdContentCopy,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import Button from '../../components/ui/Button'
import EmptyState from '../../components/ui/EmptyState'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useSimulation } from '../../hooks/useSimulation'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function SimulationDetailPage() {
  const dispatch = useDispatch()
  const { simulationNumber } = useParams()
  const navigate = useNavigate()
  const [simulation, setSimulation] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()

  const {
    getSimulationAsync,
    startSimulationAsync,
    stopSimulationAsync,
    deleteSimulationAsync,
    isGetSimulationLoading,
    isStartSimulationLoading,
    isStopSimulationLoading,
    isDeleteSimulationLoading,
  } = useSimulation()

  const loadSimulation = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await getSimulationAsync(simulationNumber)
      setSimulation(data)
    } catch (error) {
      console.error('Error loading simulation:', error)
      setError('Failed to load simulation details')
    } finally {
      setIsLoading(false)
    }
  }, [simulationNumber]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (simulationNumber) {
      loadSimulation()
    }
  }, [simulationNumber, loadSimulation])

  useEffect(() => {
    const channelName = getWebsocketChannel(simulationNumber?.toLowerCase()).simulationDetails

    if (channelName && simulationNumber) {
      dispatch(addSubscription(channelName))
      subscribeToChannel(channelName)
    }

    return () => {
      if (channelName && simulationNumber) {
        dispatch(removeSubscription(channelName))
        unsubscribeFromChannel(channelName)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartSimulation = async () => {
    try {
      await startSimulationAsync(simulationNumber)
      await loadSimulation() // Refresh the data
    } catch (error) {
      console.error('Error starting simulation:', error)
    }
  }

  const handleStopSimulation = async () => {
    try {
      await stopSimulationAsync(simulationNumber)
      await loadSimulation() // Refresh the data
    } catch (error) {
      console.error('Error stopping simulation:', error)
    }
  }

  const handleDeleteSimulation = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete this simulation? This action cannot be undone.',
      )
    ) {
      try {
        await deleteSimulationAsync(simulationNumber)
        navigate('/simulations')
      } catch (error) {
        console.error('Error deleting simulation:', error)
      }
    }
  }

  const handleCopySimulationNumber = async () => {
    try {
      await navigator.clipboard.writeText(simulation.simulationNumber)
      // You could add a toast notification here if you have a toast system
      console.log('Simulation number copied to clipboard')
    } catch (error) {
      console.error('Failed to copy simulation number:', error)
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'running':
        return <MdPlayArrow className="text-success-600 dark:text-success-400" />
      case 'paused':
      case 'on hold':
      case 'inactive':
        return <MdPause className="text-warning-600 dark:text-warning-400" />
      case 'completed':
      case 'finished':
      case 'stopped':
        return <MdStop className="text-neutral-600 dark:text-neutral-400" />
      default:
        return <MdSchedule className="text-neutral-500 dark:text-neutral-400" />
    }
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'running':
        return 'bg-success-100 dark:bg-success-900 text-success-600 dark:text-success-400'
      case 'paused':
      case 'on hold':
      case 'inactive':
        return 'bg-warning-100 dark:bg-warning-900 text-warning-600 dark:text-warning-400'
      case 'completed':
      case 'finished':
      case 'stopped':
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400'
    }
  }

  const formatDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 'N/A'
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffMs = end - start
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    return `${diffDays} days`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getParticipantStats = () => {
    return {
      flights: simulation.flights?.length || 0,
      pilots: simulation.pilots?.length || 0,
      passengers: simulation.passengers?.length || 0,
      aircraft: simulation.aircraft?.length || 0,
      airports: simulation.airports?.length || 0,
      airlines: simulation.airlines?.length || 0,
      flightAttendants: simulation.flightAttendants?.length || 0,
      bookings: simulation.bookings?.length || 0,
      jobs: simulation.jobs?.length || 0,
      bagages: simulation.bagages?.length || 0,
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !simulation) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="text-left">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 mb-4"
            >
              <MdArrowBack />
              <span>Back to Simulations</span>
            </Button>
          </div>

          <EmptyState
            icon={<MdChat />}
            title="Simulation Not Found"
            description={
              error || "The simulation you're looking for doesn't exist or has been removed."
            }
            action={
              <Button variant="primary" onClick={() => navigate('/simulations')}>
                Back to Simulations
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  const participantStats = getParticipantStats()

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="text-left">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 mb-4"
          >
            <MdArrowBack />
            <span>Back to Simulations</span>
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-neutral-900 dark:text-white mb-2 text-left">
              {simulation.name || `Simulation #${simulation.simulationNumber}`}
            </h1>
            <div className="flex items-center space-x-2">
              <p className="text-base lg:text-lg text-neutral-600 dark:text-neutral-400 text-left break-all">
                {simulation.simulationNumber}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopySimulationNumber}
                className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 flex-shrink-0"
                title="Copy simulation number"
              >
                <MdContentCopy className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200" />
              </Button>
            </div>
          </div>

          <div className="flex items-center space-x-3 flex-shrink-0">
            <Button variant="ghost" onClick={loadSimulation} disabled={isGetSimulationLoading}>
              <MdRefresh className={isGetSimulationLoading ? 'animate-spin' : ''} />
            </Button>

            <span
              className={`px-3 lg:px-4 py-2 text-sm font-medium rounded-full ${getStatusColor(simulation.status)}`}
            >
              {simulation.status || 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-4 lg:p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center space-x-2">
            {getStatusIcon(simulation.status)}
            <span className="text-neutral-900 dark:text-white font-medium">
              {simulation.status || 'Unknown'}
            </span>
            {simulation.version && (
              <span className="text-sm text-neutral-500 dark:text-neutral-500">
                v{simulation.version}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:gap-3">
            {simulation.status?.toLowerCase() === 'inactive' && (
              <Button
                variant="success"
                size="md"
                onClick={handleStartSimulation}
                loading={isStartSimulationLoading}
                className="flex-1 lg:flex-none"
              >
                <MdPlayArrow className="mr-2" />
                <span className="hidden sm:inline">Start Simulation</span>
                <span className="sm:hidden">Start</span>
              </Button>
            )}

            {simulation.status?.toLowerCase() === 'active' && (
              <Button
                variant="warning"
                size="md"
                onClick={handleStopSimulation}
                loading={isStopSimulationLoading}
                className="flex-1 lg:flex-none"
              >
                <MdPause className="mr-2" />
                <span className="hidden sm:inline">Pause Simulation</span>
                <span className="sm:hidden">Pause</span>
              </Button>
            )}

            {(simulation.status?.toLowerCase() === 'active' ||
              simulation.status?.toLowerCase() === 'paused') && (
              <Button
                variant="secondary"
                size="md"
                onClick={handleStopSimulation}
                loading={isStopSimulationLoading}
                className="flex-1 lg:flex-none"
              >
                <MdStop className="mr-2" />
                <span className="hidden sm:inline">Stop Simulation</span>
                <span className="sm:hidden">Stop</span>
              </Button>
            )}

            <Button
              variant="outline"
              size="md"
              onClick={() => {
                /* TODO: Implement edit */
              }}
              className="flex-1 lg:flex-none"
            >
              <MdEdit className="mr-2" />
              <span className="hidden sm:inline">Edit</span>
              <span className="sm:hidden">Edit</span>
            </Button>

            <Button
              variant="danger"
              size="md"
              onClick={handleDeleteSimulation}
              loading={isDeleteSimulationLoading}
              className="flex-1 lg:flex-none"
            >
              <MdDelete className="mr-2" />
              <span className="hidden sm:inline">Delete</span>
              <span className="sm:hidden">Delete</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Simulation Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">
              Basic Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Simulation Number
                </label>
                <p className="text-neutral-900 dark:text-white font-medium">
                  {simulation.simulationNumber?.slice(0, 15)}...
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Status
                </label>
                <p className="text-neutral-900 dark:text-white font-medium">
                  <span className="inline-flex items-center space-x-2">
                    {getStatusIcon(simulation.status)}
                    <span>{simulation.status || 'Unknown'}</span>
                  </span>
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Duration
                </label>
                <p className="text-neutral-900 dark:text-white font-medium">
                  {formatDuration(simulation.startDate, simulation.endDate)}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Version
                </label>
                <p className="text-neutral-900 dark:text-white font-medium">
                  {simulation.version || 'N/A'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Start Date
                </label>
                <p className="text-neutral-900 dark:text-white font-medium">
                  {formatDate(simulation.startDate)}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  End Date
                </label>
                <p className="text-neutral-900 dark:text-white font-medium">
                  {formatDate(simulation.endDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          {simulation.description && (
            <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">
                Description
              </h2>
              <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                {simulation.description}
              </p>
            </div>
          )}

          {/* Configuration */}
          {simulation.configuration && (
            <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">
                Configuration
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {simulation.configuration.weatherConditions && (
                  <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                    <h3 className="font-medium text-neutral-900 dark:text-white mb-2">
                      Weather Conditions
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 capitalize">
                      {simulation.configuration.weatherConditions}
                    </p>
                  </div>
                )}

                {simulation.configuration.passengerLoad && (
                  <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                    <h3 className="font-medium text-neutral-900 dark:text-white mb-2">
                      Passenger Load
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 capitalize">
                      {simulation.configuration.passengerLoad}
                    </p>
                  </div>
                )}

                {simulation.configuration.operationalMode && (
                  <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                    <h3 className="font-medium text-neutral-900 dark:text-white mb-2">
                      Operational Mode
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 capitalize">
                      {simulation.configuration.operationalMode}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tags */}
          {simulation.tags && simulation.tags.length > 0 && (
            <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {simulation.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400"
                  >
                    <MdTag className="mr-1" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* History */}
          {simulation.history && simulation.history.length > 0 && (
            <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">History</h2>
                {simulation.history.length > 5 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/simulations/${simulationNumber}/history`)}
                  >
                    View All History
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {simulation.history.slice(0, 5).map((entry, index) => (
                  <div
                    key={entry._id || index}
                    className="flex items-center space-x-3 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg"
                  >
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="font-medium text-neutral-900 dark:text-white capitalize">
                        {entry.action?.replace(/_/g, ' ').toLowerCase()}
                      </p>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {formatDate(entry.timestamp)}
                      </p>
                      {entry.details && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                          {entry.details.status && `Status: ${entry.details.status}`}
                          {entry.details.createdBy && `Created by: ${entry.details.createdBy}`}
                          {entry.details.initialStatus &&
                            `Initial status: ${entry.details.initialStatus}`}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {simulation.history.length > 5 && (
                <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700 text-center">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Showing 5 of {simulation.history.length} history entries
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Quick Stats
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Status</span>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(simulation.status)}`}
                >
                  {simulation.status || 'Unknown'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Duration</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {formatDuration(simulation.startDate, simulation.endDate)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Version</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {simulation.version || 'N/A'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Archived</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {simulation.isArchived ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* Participants */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Participants
            </h3>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
                  <MdFlight className="mr-2" />
                  Flights
                </span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {participantStats.flights}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
                  <MdPerson className="mr-2" />
                  Pilots
                </span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {participantStats.pilots}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
                  <MdPerson className="mr-2" />
                  Passengers
                </span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {participantStats.passengers}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
                  <MdAirplanemodeActive className="mr-2" />
                  Aircraft
                </span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {participantStats.aircraft}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
                  <MdLocationOn className="mr-2" />
                  Airports
                </span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {participantStats.airports}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
                  <MdBusiness className="mr-2" />
                  Airlines
                </span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {participantStats.airlines}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
                  <MdWork className="mr-2" />
                  Flight Attendants
                </span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {participantStats.flightAttendants}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
                  <MdLuggage className="mr-2" />
                  Baggage
                </span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {participantStats.bagages}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SimulationDetailPage
